import { motion } from "framer-motion";
import { PackageOpen, Truck, Sparkles } from "lucide-react";

const STEPS = [
  {
    n: "01",
    icon: PackageOpen,
    title: "Set Out Your Bags",
    body: "Place your tied trash bags outside your door during your selected window — morning or evening. That's your entire job.",
  },
  {
    n: "02",
    icon: Truck,
    title: "The Valet Sweep",
    body: "Our uniformed team walks every floor, collects each bag, and disposes of it properly. Quietly, nightly, on schedule.",
  },
  {
    n: "03",
    icon: Sparkles,
    title: "Pristine Hallways",
    body: "Hallways stay clean, odor-free, and clutter-free. The building breathes easier — and so do you.",
  },
];

export default function HowItWorks() {
  return (
    <section id="how-it-works" data-testid="how-it-works-section" className="relative py-28 sm:py-36">
      <div className="max-w-7xl mx-auto px-6">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="font-mono text-xs uppercase tracking-[0.3em] text-terracotta mb-5"
        >
          How it works
        </motion.p>
        <motion.h2
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="font-display font-bold tracking-tight text-2xl sm:text-3xl lg:text-4xl max-w-xl leading-tight"
        >
          Three steps between you and a <span className="text-stroke">spotless corridor.</span>
        </motion.h2>

        <div className="mt-16 grid md:grid-cols-3 gap-5">
          {STEPS.map((s, i) => (
            <motion.div
              key={s.n}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.7, delay: i * 0.15 }}
              whileHover={{ y: -8 }}
              data-testid={`how-step-${s.n}`}
              className="group relative rounded-3xl border border-white/8 bg-white/[0.03] p-8 overflow-hidden transition-colors duration-500 hover:border-terracotta/40"
            >
              <span className="absolute -top-4 -right-2 font-display font-extrabold text-[7rem] leading-none text-white/[0.04] group-hover:text-terracotta/10 transition-colors duration-500 select-none">
                {s.n}
              </span>
              <div className="w-11 h-11 rounded-full bg-forest flex items-center justify-center mb-16">
                <s.icon className="w-5 h-5 text-parchment" strokeWidth={1.8} />
              </div>
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-terracotta mb-3">Step {s.n}</p>
              <h3 className="font-display font-bold text-xl mb-3">{s.title}</h3>
              <p className="text-sm text-mist leading-relaxed">{s.body}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
